'use client';

// CALENDARIO de comités de queja (ago-06, pedido del cliente). Muestra, en el día
// en que quedó programado el comité, SOLO el radicado del caso; si la solicitud no
// tiene radicado —hoy pasa con el canal FÍSICO, que no maneja ninguno— se escribe
// por dónde ingresó («Ingresó por físico»).
//
// La fecha sale de la Etapa 1 (Control de ingreso → ruta COMITÉ DE QUEJA): se usa
// la «fecha del comité» y, si el comité se REPROGRAMÓ, la fecha nueva.
// Lo ven el Técnico administrativo, su apoyo y el Director de Instrucción.

import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { avisoError } from '@/lib/avisoCentral';
import HeroVista from '@/components/HeroVista';
import {
  calendarioComites, etiquetaEvento, labelDecision, labelCanal, comiteCierra,
  type EventoComite,
} from '@/lib/preRadicacion';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
// La semana arranca en lunes (uso local).
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/** Clave YYYY-MM-DD de una fecha ISO, leída como día calendario (sin husos). */
function claveDia(iso: string): string {
  return iso.slice(0, 10);
}

function claveDe(anio: number, mes: number, dia: number): string {
  return `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** Lunes = 0 … domingo = 6 (getDay() devuelve domingo = 0). */
function primerDiaSemana(anio: number, mes: number): number {
  return (new Date(anio, mes, 1).getDay() + 6) % 7;
}

// Cuántos radicados se asoman en la celda del día. El resto se lee en el modal:
// un día puede tener 10 o más y la cuadrícula se desbordaría.
const MAX_EN_CELDA = 2;

/** «miércoles, 12 de agosto de 2026» a partir de la clave YYYY-MM-DD. */
function fechaEnLetras(clave: string): string {
  const [a, m, d] = clave.split('-').map(Number);
  return new Date(a, m - 1, d).toLocaleDateString('es-CO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

export default function Calendario() {
  const [eventos, setEventos] = useState<EventoComite[]>([]);
  const [cargando, setCargando] = useState(true);
  // Día abierto en el modal (clave YYYY-MM-DD) con su lista completa.
  const [diaAbierto, setDiaAbierto] = useState<string | null>(null);
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth());

  async function cargar() {
    setCargando(true);
    try {
      setEventos(await calendarioComites());
    } catch (e) {
      avisoError('No se pudo cargar el calendario', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  // Eventos agrupados por día (una sola pasada; el mes se arma leyendo el mapa).
  const porDia = useMemo(() => {
    const m = new Map<string, EventoComite[]>();
    for (const e of eventos) {
      const k = claveDia(e.fecha);
      const lista = m.get(k);
      if (lista) lista.push(e); else m.set(k, [e]);
    }
    return m;
  }, [eventos]);

  const diasDelMes = new Date(anio, mes + 1, 0).getDate();
  const huecoInicial = primerDiaSemana(anio, mes);
  const celdas: Array<number | null> = [
    ...Array.from({ length: huecoInicial }, () => null),
    ...Array.from({ length: diasDelMes }, (_, i) => i + 1),
  ];

  const delMes = eventos.filter((e) => {
    const [a, m] = claveDia(e.fecha).split('-').map(Number);
    return a === anio && m === mes + 1;
  });

  function mover(delta: number) {
    const d = new Date(anio, mes + delta, 1);
    setAnio(d.getFullYear());
    setMes(d.getMonth());
  }

  const claveHoy = claveDe(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  return (
    <div className="space-y-6">
      <HeroVista
        icono={CalendarDays}
        titulo="Calendario"
        descripcion="Comités de queja programados desde el Control de ingreso. Si un comité se reprogramó, aparece en la fecha nueva."
        acciones={
          <Button
            variant="outline"
            className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20"
            onClick={cargar}
          >
            <RefreshCw className="size-4" /> Actualizar
          </Button>
        }
      />

      {/* Barra del mes */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => mover(-1)} aria-label="Mes anterior">
            <ChevronLeft className="size-4" />
          </Button>
          <p className="min-w-44 text-center text-lg font-semibold">
            {MESES[mes]} {anio}
          </p>
          <Button variant="outline" size="icon" onClick={() => mover(1)} aria-label="Mes siguiente">
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setAnio(hoy.getFullYear()); setMes(hoy.getMonth()); }}
          >
            Ir a hoy
          </Button>
          <Badge variant="outline">
            {delMes.length === 1 ? '1 comité este mes' : `${delMes.length} comités este mes`}
          </Badge>
        </div>
      </div>

      <Card className="py-2">
        <CardContent className="px-2">
          {cargando ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 border-b pb-2">
                {DIAS.map((d) => (
                  <p key={d} className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {d}
                  </p>
                ))}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {celdas.map((dia, i) => {
                  if (dia === null) return <div key={`v-${i}`} className="min-h-24 rounded-md" />;
                  const clave = claveDe(anio, mes, dia);
                  const delDia = porDia.get(clave) ?? [];
                  const esHoy = clave === claveHoy;
                  const conEventos = delDia.length > 0;
                  // Solo se asoman los primeros; el resto se ve en el modal del
                  // día (puede haber 10 o más y la cuadrícula se desbordaría).
                  const visibles = delDia.slice(0, MAX_EN_CELDA);
                  const ocultos = delDia.length - visibles.length;
                  return (
                    <div
                      key={clave}
                      role={conEventos ? 'button' : undefined}
                      tabIndex={conEventos ? 0 : undefined}
                      onClick={conEventos ? () => setDiaAbierto(clave) : undefined}
                      onKeyDown={conEventos
                        ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setDiaAbierto(clave); } }
                        : undefined}
                      title={conEventos
                        ? `Ver ${delDia.length === 1 ? 'el comité' : `los ${delDia.length} comités`} de este día`
                        : undefined}
                      className={cn(
                        'min-h-24 rounded-md border p-1.5 align-top transition',
                        esHoy ? 'border-primary/50 bg-primary/[0.04]' : 'bg-card',
                        conEventos && 'cursor-pointer hover:border-primary/60 hover:bg-primary/[0.06]'
                          + ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between gap-1">
                        <span className={cn(
                          'text-xs font-semibold',
                          esHoy ? 'text-primary' : 'text-muted-foreground',
                        )}>
                          {dia}
                        </span>
                        {conEventos && (
                          <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                            {delDia.length}
                          </span>
                        )}
                      </div>
                      <ul className="space-y-1">
                        {visibles.map((e) => (
                          <li
                            key={e.solicitud_id}
                            className={cn(
                              'truncate rounded border px-1.5 py-0.5 text-[11px] font-medium',
                              // Si el comité ya cerró el proceso se marca en rojo suave.
                              comiteCierra(e.decision)
                                ? 'border-destructive/30 bg-destructive/5 text-destructive'
                                : 'border-primary/30 bg-primary/5 text-primary',
                            )}
                          >
                            {etiquetaEvento(e)}
                          </li>
                        ))}
                      </ul>
                      {ocultos > 0 && (
                        <p className="mt-1 text-[11px] font-medium text-muted-foreground">
                          +{ocultos} más
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Sin lista del mes debajo: con muchos comités se volvía interminable —
          justo lo que se quiere evitar. El detalle vive en el modal del día. */}

      {!cargando && eventos.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Todavía no hay comités de queja programados. Se agendan desde «Control de ingreso»
          al enviar un caso al comité.
        </p>
      )}

      {/* Modal del día: la lista COMPLETA de expedientes citados a comité. */}
      {diaAbierto && (
        <ModalDia
          clave={diaAbierto}
          eventos={porDia.get(diaAbierto) ?? []}
          onClose={() => setDiaAbierto(null)}
        />
      )}
    </div>
  );
}

/**
 * Lista completa de los expedientes citados a comité ese día. Mismo lenguaje
 * visual del modal de «Asignar proceso»: encabezado centrado con ícono circular
 * verde y filas en tarjeta de delineado verde. La lista hace scroll propio para
 * aguantar 10 o más sin estirar el diálogo.
 */
function ModalDia({
  clave, eventos, onClose,
}: {
  clave: string;
  eventos: EventoComite[];
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-1 flex size-12 items-center justify-center rounded-full bg-primary/10">
            <CalendarDays className="size-6 text-primary" />
          </div>
          <DialogTitle className="text-center capitalize">{fechaEnLetras(clave)}</DialogTitle>
          <DialogDescription className="text-center">
            {eventos.length === 1
              ? '1 expediente citado a comité de queja'
              : `${eventos.length} expedientes citados a comité de queja`}
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {eventos.map((e, i) => (
            <li
              key={e.solicitud_id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/25 bg-primary/[0.02] p-3"
            >
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn(
                  'truncate font-medium',
                  // Sin radicado el texto es descriptivo («Ingresó por físico»):
                  // se muestra en gris para no confundirlo con un radicado real.
                  e.radicado ? 'text-primary' : 'italic text-muted-foreground',
                )}>
                  {etiquetaEvento(e)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Ingresó por {labelCanal(e.canal).toLowerCase()}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                {e.reprogramado && <Badge variant="outline">Reprogramado</Badge>}
                <Badge
                  variant={e.decision ? 'secondary' : 'outline'}
                  className={cn(comiteCierra(e.decision) && 'bg-destructive/10 text-destructive')}
                >
                  {e.decision ? labelDecision(e.decision) : 'Sin decisión'}
                </Badge>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex justify-center pt-1">
          <Button type="button" variant="outline" className="rounded-full px-6" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
